import { AthenaEvent } from "@evidenceb/athena-event-storage-schemas";
import {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  isAxiosError,
} from "axios";
import {
  makeBackendUnreachable,
  makeUnknown,
} from "../AthenaEventSaveError/AthenaEventSaveError.js";
import { Milliseconds } from "../utils/Milliseconds.js";
import { extractThrownValueDetails } from "../utils/extractThrownValueDetails.js";
import { AthenaEventSaveResult } from "./AthenaEventSaveResult.js";
import { EventStorageClient } from "./EventStorageClient.js";
import {
  BackendUnreachable,
  InvalidFetchAthenaEventsFilters,
} from "./errors.js";
import {
  GetEvents200ResponseBody,
  toDateQueryParam,
} from "./http/GetEvents.js";
import {
  PostEventsRequestBody,
  PostEventsResponseBody,
  toAthenaEventSaveResults,
} from "./http/PostEvents.js";

/**
 * Http client for the `EvidenceB/event-storage` service.
 */
export class HttpEventStorageClient implements EventStorageClient {
  constructor(
    public eventStorageOrigin: string,
    private axiosInstance: AxiosInstance,
    /** Function to retrieves an auth token to use when calling the service. */
    private getAuthToken: () => string,
  ) {}

  async saveAthenaEvents(
    athenaEvents: AthenaEvent<any, any>[],
    options?: {
      /** Timeout for the save operation in milliseconds. */
      timeout?: Milliseconds;
    },
  ): Promise<AthenaEventSaveResult[]> {
    try {
      const { status, data } = await this.axiosInstance.post<
        PostEventsResponseBody,
        AxiosResponse<PostEventsResponseBody>,
        PostEventsRequestBody
      >(
        "/api/v1/events/",
        athenaEvents,
        this.extendBaseConfig({
          timeout: options?.timeout ?? 0,
          // Don't throw an error for the following status codes.
          validateStatus: (status) => [200, 400].includes(status),
        }),
      );
      return toAthenaEventSaveResults(
        { status: status as 200 | 400, body: data },
        { body: athenaEvents },
      );
    } catch (error) {
      // When the error originates from the axios HTTP request and the request
      // has no response attached, it means that the backend is unreachable for
      // some reason. Possible reasons include:
      //
      // - The user is offline.
      // - The DNS for the backend could not resolve.
      // - The backend server is down.
      // - The backend server is taking too long to response, so axios timed out
      //   the request.
      // - The browser blocked the request due to CORS, which prevents the
      //   javascript context to access the response object, which results
      //   undefined.
      if (isAxiosError(error) && error.response === undefined) {
        return athenaEvents.map((athenaEvent) => ({
          athenaEvent: athenaEvent,
          succeeded: false,
          error: makeBackendUnreachable(extractThrownValueDetails(error)),
        }));
      }
      // If the error is unknown, report that all `AthenaEvent`s failed saving
      // with an `Unknown` error.
      return athenaEvents.map((athenaEvent) => ({
        athenaEvent: athenaEvent,
        succeeded: false,
        error: makeUnknown(extractThrownValueDetails(error)),
      }));
    }
  }

  async fetchAthenaEvents(filters: {
    userId: string;
    /** Where `AthenaEvents.createdAt >= since` */
    since?: Date;
    /** Where `AthenaEvents.insertedAt >= since` */
    insertedAfter?: Date | undefined;
    athenaEventTypes?: string[] | undefined;
    /** Note: not all `AthenaEvent`s have a `learningSetId`. */
    learningSetId?: string | undefined;
    /** Note: not all `AthenaEvent`s have a `workmode`. */
    workmode?: string | undefined;
  }): Promise<AthenaEvent<any, any>[]> {
    try {
      const { data } = await this.axiosInstance.get<GetEvents200ResponseBody>(
        `/api/v1/events/user/${filters.userId}`,
        this.extendBaseConfig({
          params: {
            learning_set_id: filters.learningSetId,
            workmode: filters.workmode,
            since: toDateQueryParam(filters.since),
            inserted_after: toDateQueryParam(filters.insertedAfter),
            event_types: filters.athenaEventTypes,
          },
        }),
      );
      return data;
    } catch (error) {
      // When the error originates from the axios HTTP request, and the request
      // received no response, it means that the backend is unreachable.
      if (isAxiosError(error) && error.response === undefined) {
        throw new BackendUnreachable(error);
      }
      if (isAxiosError(error) && error.response?.status === 422) {
        throw new InvalidFetchAthenaEventsFilters(filters, error.response.data);
      }
      throw error;
    }
  }

  private extendBaseConfig<Data = any>(
    config: AxiosRequestConfig<Data>,
  ): AxiosRequestConfig<Data> {
    return {
      ...config,
      baseURL: this.eventStorageOrigin,
      headers: {
        ...config.headers,
        Authorization: `Bearer ${this.getAuthToken()}`,
      },
      paramsSerializer: { indexes: null },
    };
  }
}
